import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/context/LanguageContext";
import { ProfileData } from "@/types/profile";
import ProfileForm from "@/components/signup/ProfileForm";
import { Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const SignupComplete = () => {
  // 表單狀態
  const [formData, setFormData] = useState<ProfileData>({
    full_name: "",
    nickname: "",
    country: "",
    age: null,
    gender: null,
  });
  
  // 元件狀態
  const [isLoading, setIsLoading] = useState(false);
  const [hasExistingProfile, setHasExistingProfile] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // Hooks
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();

  // 初始化
  useEffect(() => {
    // 從 URL 參數獲取邀請碼
    const params = new URLSearchParams(location.search);
    const urlInviteCode = params.get("invite") || params.get("ref");
    if (urlInviteCode) {
      setInviteCode(urlInviteCode);
    }
    
    handleEmailVerification();
  }, []);

  // 處理電子郵件驗證
  const handleEmailVerification = async () => {
    const params = new URLSearchParams(location.search);
    const tokenHash = params.get("token_hash");
    const type = params.get("type");

    if (tokenHash && type) {
      setIsLoading(true);
      console.log("使用 token_hash 驗證電子郵件:", tokenHash, "type:", type);

      try {
        const { data, error } = await supabase.auth.verifyOtp({
          type: type as any,
          token_hash: tokenHash,
        });
        
        console.log('OTP 驗證結果:', { data, error });

        if (error) {
          console.error("OTP 驗證錯誤:", error);
          if (error.message?.includes("expired") || error.message?.includes("invalid")) {
            setErrorMsg("驗證連結已過期或無效。請重新註冊或要求新的驗證連結。");
          } else {
            setErrorMsg(error.message || "驗證失敗，請重試。");
          }
          setIsEmailVerified(false);
        } else {
          console.log("OTP 驗證成功:", data);
          setIsEmailVerified(true);
          // 等待一下讓認證狀態更新
          setTimeout(() => {
            checkAuth();
          }, 1000);
        }
      } catch (err: any) {
        console.error("電子郵件驗證錯誤:", err);
        setErrorMsg(err.message || "驗證過程發生錯誤，請重試。");
        setIsEmailVerified(false);
      } finally {
        setIsLoading(false);
      }
    } else {
      console.log("URL 中沒有 token_hash，直接檢查會話");
      checkAuth();
    }
  };

  // 檢查認證狀態
  const checkAuth = async () => {
    try {
      setCheckingProfile(true);
      setErrorMsg(null);
      
      console.log("開始檢查認證狀態，重試次數:", retryCount);
      
      // 獲取當前會話，增加重試機制
      let session = null;
      let sessionError = null;
      
      for (let i = 0; i < 3; i++) {
        const { data: sessionData, error: err } = await supabase.auth.getSession();
        session = sessionData.session;
        sessionError = err;
        
        if (session || !err) break;
        
        console.log(`會話檢查重試 ${i + 1}/3`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      console.log('檢查認證: 會話資料:', session);
      console.log('檢查認證: 會話錯誤:', sessionError);

      if (sessionError) {
        console.error('檢查認證: 會話錯誤:', sessionError);
        throw new Error('獲取登入狀態時發生錯誤：' + sessionError.message);
      }

      if (!session || !session.user) {
        console.log('檢查認證: 沒有找到有效會話');
        
        // 如果重試次數少於3次，等待後重試
        if (retryCount < 3) {
          console.log('重試檢查認證狀態...');
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            checkAuth();
          }, 2000);
          return;
        }
        
        throw new Error('請先登入或完成電子郵件驗證後再繼續。如果您剛完成驗證，請稍等片刻再重新整理頁面。');
      }

      const userId = session.user.id;
      console.log('檢查認證: 找到使用者 ID:', userId);

      // 檢查 profiles 表中的使用者資料
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('full_name, nickname, country, age, gender, is_verified, referrer_id')
        .eq('id', userId)
        .maybeSingle();

      console.log('檢查認證: 個人資料資料:', profileData);
      console.log('檢查認證: 個人資料錯誤:', profileError);

      if (profileError) {
        console.error('檢查認證: 個人資料取得錯誤:', profileError);
        throw new Error('檢查個人資料時發生錯誤：' + profileError.message);
      }

      // 如果使用者已經完成驗證，直接導向首頁
      if (profileData && profileData.is_verified === true) {
        console.log('檢查認證: 個人資料已驗證，導向首頁');
        navigate('/', { replace: true });
        return;
      }

      // 處理邀請碼邏輯
      if (inviteCode && session.user.user_metadata?.referrer_id) {
        console.log('處理邀請碼:', inviteCode);
        await handleInviteCode(userId, session.user.user_metadata.referrer_id);
      }

      // 如果有部分資料，填入表單
      if (profileData) {
        console.log('檢查認證: 找到現有個人資料資料，填入表單');
        setFormData({
          full_name: profileData.full_name || '',
          nickname: profileData.nickname || '',
          country: profileData.country || '',
          age: profileData.age !== null && profileData.age !== undefined ? profileData.age : null,
          gender: profileData.gender || '',
        });
        setHasExistingProfile(true);
      } else {
        console.log('檢查認證: 沒有找到個人資料，將建立新的');
      }

      // 重置重試計數器
      setRetryCount(0);

    } catch (error: any) {
      console.error("檢查認證時發生錯誤:", error);
      setErrorMsg(error.message || '發生預期外的錯誤，請重試。');
    } finally {
      setCheckingProfile(false);
    }
  };

  // 處理邀請碼
  const handleInviteCode = async (userId: string, referrerId: string) => {
    try {
      // 更新個人資料中的邀請人 ID
      const { error } = await supabase
        .from('profiles')
        .update({ referrer_id: referrerId })
        .eq('id', userId);

      if (error) {
        console.error('更新邀請人 ID 錯誤:', error);
      } else {
        console.log('成功設定邀請人 ID:', referrerId);
      }
    } catch (error) {
      console.error('處理邀請碼錯誤:', error);
    }
  };

  // 語言選項
  const languages = [
    { code: "zh-TW", label: "繁體中文" },
    { code: "en", label: "English" }
  ];

  // 處理語言變更
  const handleLanguageChange = (langCode: 'en' | 'zh-TW') => {
    setLanguage(langCode);
  };

  // 處理重試
  const handleRetry = () => {
    setRetryCount(0);
    setErrorMsg(null);
    setCheckingProfile(true);
    checkAuth();
  };

  // 載入中畫面
  if (checkingProfile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-700">
            {language === 'en' ? 'Checking account status, please wait...' : '正在檢查帳號狀態，請稍候…'}
          </p>
          {retryCount > 0 && (
            <p className="text-sm text-gray-500 mt-2">
              重試中... ({retryCount}/3)
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md space-y-8 rounded-xl bg-white p-8 shadow-lg">
        {/* 語言切換按鈕 */}
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="w-9 px-0">
                <Globe className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {languages.map(lang => (
                <DropdownMenuItem 
                  key={lang.code} 
                  onClick={() => handleLanguageChange(lang.code as 'en' | 'zh-TW')} 
                  className={language === lang.code ? "bg-accent" : ""}
                >
                  {lang.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* 頁面標題 */}
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight">{t('auth.completeProfile')}</h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('auth.pleaseFillProfile')}
          </p>
          {inviteCode && (
            <p className="mt-2 text-sm text-green-600">
              使用邀請碼：{inviteCode}
            </p>
          )}
        </div>

        {/* 錯誤訊息 */}
        {errorMsg && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
            {errorMsg}
            <div className="mt-2 space-y-2">
              <button
                onClick={handleRetry}
                className="w-full bg-primary text-white py-2 px-4 rounded hover:bg-primary/90"
              >
                重試
              </button>
              <button
                onClick={() => navigate("/login")}
                className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded hover:bg-gray-300"
              >
                重新登入
              </button>
              <button
                onClick={() => navigate("/")}
                className="w-full bg-gray-100 text-gray-600 py-2 px-4 rounded hover:bg-gray-200"
              >
                返回首頁
              </button>
            </div>
          </div>
        )}

        {/* 電子郵件驗證狀態 */}
        {isEmailVerified === false && (
          <div className="mb-4 p-3 bg-yellow-100 text-yellow-800 rounded">
            <p className="font-medium">
              {language === 'en' ? 'Your email has not been verified yet' : '您的電子郵件尚未驗證'}
            </p>
            <p className="text-sm mt-1">
              {language === 'en' 
                ? 'Please check your email inbox and click the verification link to complete verification.' 
                : '請檢查您的電子郵件信箱，點擊驗證連結完成驗證。'}
            </p>
          </div>
        )}

        {isEmailVerified === true && (
          <div className="mb-4 p-3 bg-green-100 text-green-800 rounded">
            <p className="font-medium">
              {language === 'en' ? 'Your email has been verified successfully' : '您的電子郵件已驗證成功'}
            </p>
          </div>
        )}

        {/* 個人資料表單 */}
        {!errorMsg && (
          <ProfileForm
            initialData={formData}
            isSignup={true}
          />
        )}
      </div>
    </div>
  );
};

export default SignupComplete;
